import type { ClientTitle, CreateClientDetailsDto } from '@nestjs-boilerplate/types';
import { ChevronDown, Info } from 'lucide-react';
import type { Control } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TitleGroup {
  category: string;
  titles: { value: ClientTitle; label: string }[];
}

const TITLE_GROUPS: TitleGroup[] = [
  {
    category: 'Civil',
    titles: [
      { value: 'MR', label: 'Mr' },
      { value: 'MRS', label: 'Mrs' },
      { value: 'MISS', label: 'Miss' },
      { value: 'MS', label: 'Ms' },
      { value: 'MX', label: 'Mx' },
    ],
  },
  {
    category: 'Professional/Academic',
    titles: [
      { value: 'DR', label: 'Dr' },
      { value: 'PROF', label: 'Prof' },
    ],
  },
  {
    category: 'Religious',
    titles: [
      { value: 'REV', label: 'Rev' },
      { value: 'FR', label: 'Fr' },
      { value: 'SR', label: 'Sr' },
      { value: 'BR', label: 'Br' },
      { value: 'BISHOP', label: 'Bishop' },
      { value: 'ARCHBISHOP', label: 'Archbishop' },
      { value: 'CARDINAL', label: 'Cardinal' },
      { value: 'CANON', label: 'Canon' },
      { value: 'IMAM', label: 'Imam' },
      { value: 'SHEIKH', label: 'Sheikh' },
      { value: 'RABBI', label: 'Rabbi' },
      { value: 'PASTOR', label: 'Pastor' },
    ],
  },
  {
    category: 'Honour/Peerage/Political',
    titles: [
      { value: 'SIR', label: 'Sir' },
      { value: 'DAME', label: 'Dame' },
      { value: 'LORD', label: 'Lord' },
      { value: 'LADY', label: 'Lady' },
      { value: 'BARON', label: 'Baron' },
      { value: 'BARONESS', label: 'Baroness' },
      { value: 'THE_HON', label: 'The Hon' },
      { value: 'THE_RT_HON', label: 'The Rt Hon' },
    ],
  },
  {
    category: 'Judicial',
    titles: [
      { value: 'JUDGE', label: 'Judge' },
      { value: 'JUSTICE', label: 'Justice' },
      { value: 'MAGISTRATE', label: 'Magistrate' },
    ],
  },
  {
    category: 'Military',
    titles: [
      { value: 'CAPT', label: 'Capt' },
      { value: 'CMDR', label: 'Cmdr' },
      { value: 'COL', label: 'Col' },
      { value: 'MAJ', label: 'Maj' },
      { value: 'LT', label: 'Lt' },
      { value: 'SGT', label: 'Sgt' },
      { value: 'CPL', label: 'Cpl' },
      { value: 'WO', label: 'WO' },
      { value: 'ADM', label: 'Adm' },
      { value: 'AIR_CDRE', label: 'Air Cdre' },
      { value: 'GP_CAPT', label: 'Gp Capt' },
      { value: 'FLT_LT', label: 'Flt Lt' },
    ],
  },
  {
    category: 'Royal',
    titles: [
      { value: 'HM', label: 'HM' },
      { value: 'HRH', label: 'HRH' },
    ],
  },
  {
    category: 'Formal',
    titles: [{ value: 'ESQ', label: 'Esq' }],
  },
];

// Flatten for lookup purposes
const ALL_TITLES = TITLE_GROUPS.flatMap((group) => group.titles);

interface TitleMultiSelectProps {
  control: Control<CreateClientDetailsDto>;
}

export function TitleMultiSelect({ control }: TitleMultiSelectProps) {
  return (
    <FormField
      control={control}
      name="titles"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="flex items-center gap-2">
            Title(s)
            <Tooltip>
              <TooltipTrigger type="button">
                <Info className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Select multiple titles if needed (e.g., for name changes over time).</p>
              </TooltipContent>
            </Tooltip>
          </FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  className={cn(
                    'w-full justify-between font-normal overflow-hidden',
                    !field.value?.length && 'text-muted-foreground',
                  )}
                >
                  <span className="truncate">
                    {field.value?.length
                      ? field.value
                          .map((v) => ALL_TITLES.find((o) => o.value === v)?.label)
                          .join(', ')
                      : 'Select title(s)'}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="max-h-[300px] w-[220px] overflow-y-auto p-0" align="start">
              <div className="p-2" role="listbox">
                {TITLE_GROUPS.map((group, groupIndex) => (
                  <div key={group.category}>
                    {groupIndex > 0 && <div className="my-1 border-t border-gray-200" />}
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                      {group.category}
                    </div>
                    {group.titles.map((option) => {
                      const handleToggle = () => {
                        const currentValues = field.value ?? [];
                        if (currentValues.includes(option.value)) {
                          field.onChange(currentValues.filter((v) => v !== option.value));
                        } else {
                          field.onChange([...currentValues, option.value]);
                        }
                      };
                      return (
                        <div
                          key={option.value}
                          role="option"
                          tabIndex={0}
                          aria-selected={field.value?.includes(option.value) ?? false}
                          className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-gray-100"
                          onClick={handleToggle}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleToggle();
                            }
                          }}
                        >
                          <Checkbox
                            checked={field.value?.includes(option.value) ?? false}
                            tabIndex={-1}
                          />
                          <span className="text-sm">{option.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
